/**
 * Localized blog data loader — performance-optimised.
 *
 * Previous version used `require()` inside a loop, which forced webpack to
 * bundle all 27 locale files (≈30 MB combined) into the main JS chunk —
 * shipped to every visitor regardless of their language.
 *
 * This version:
 *   • Imports only the Dutch source synchronously (1 MB, served as fallback)
 *   • Uses dynamic `import()` for every other locale → webpack code-splits
 *     each locale into its own chunk, loaded ONLY when needed
 *   • Caches loaded locales in memory so a page navigation within the same
 *     language doesn't re-fetch
 *
 * Backwards-compatible: `getBlog(locale)` still returns the dictionary
 * synchronously for NL or when the locale is already cached. The new
 * `getBlogAsync(locale)` returns a Promise — use it in `useEffect` for
 * non-NL pages with a loading state.
 */
import { BLOG as BLOG_NL } from "@/data/blog";

const cache = { nl: BLOG_NL };
const pending = {}; // locale → Promise (deduplicates concurrent loads)

const LOCALES = [
  "en", "de", "fr", "es", "it", "pt", "pl", "sv", "da", "fi", "no",
  "cs", "sk", "hu", "ro", "bg", "el", "et", "lv", "lt", "hr", "sl", "ga",
  "mt", "zh", "ar",
];

// ── Dynamic loader map ───────────────────────────────────────────────────
// Webpack reads the explicit module path inside each arrow function and
// creates an independent chunk per locale. Plain object lookup is O(1).
const loaders = {
  en: () => import(/* webpackChunkName: "blog-en" */ "@/data/blog.en.js"),
  de: () => import(/* webpackChunkName: "blog-de" */ "@/data/blog.de.js"),
  fr: () => import(/* webpackChunkName: "blog-fr" */ "@/data/blog.fr.js"),
  es: () => import(/* webpackChunkName: "blog-es" */ "@/data/blog.es.js"),
  it: () => import(/* webpackChunkName: "blog-it" */ "@/data/blog.it.js"),
  pt: () => import(/* webpackChunkName: "blog-pt" */ "@/data/blog.pt.js"),
  pl: () => import(/* webpackChunkName: "blog-pl" */ "@/data/blog.pl.js"),
  sv: () => import(/* webpackChunkName: "blog-sv" */ "@/data/blog.sv.js"),
  da: () => import(/* webpackChunkName: "blog-da" */ "@/data/blog.da.js"),
  fi: () => import(/* webpackChunkName: "blog-fi" */ "@/data/blog.fi.js"),
  no: () => import(/* webpackChunkName: "blog-no" */ "@/data/blog.no.js"),
  cs: () => import(/* webpackChunkName: "blog-cs" */ "@/data/blog.cs.js"),
  sk: () => import(/* webpackChunkName: "blog-sk" */ "@/data/blog.sk.js"),
  hu: () => import(/* webpackChunkName: "blog-hu" */ "@/data/blog.hu.js"),
  ro: () => import(/* webpackChunkName: "blog-ro" */ "@/data/blog.ro.js"),
  bg: () => import(/* webpackChunkName: "blog-bg" */ "@/data/blog.bg.js"),
  el: () => import(/* webpackChunkName: "blog-el" */ "@/data/blog.el.js"),
  et: () => import(/* webpackChunkName: "blog-et" */ "@/data/blog.et.js"),
  lv: () => import(/* webpackChunkName: "blog-lv" */ "@/data/blog.lv.js"),
  lt: () => import(/* webpackChunkName: "blog-lt" */ "@/data/blog.lt.js"),
  hr: () => import(/* webpackChunkName: "blog-hr" */ "@/data/blog.hr.js"),
  sl: () => import(/* webpackChunkName: "blog-sl" */ "@/data/blog.sl.js"),
  ga: () => import(/* webpackChunkName: "blog-ga" */ "@/data/blog.ga.js"),
  mt: () => import(/* webpackChunkName: "blog-mt" */ "@/data/blog.mt.js"),
  zh: () => import(/* webpackChunkName: "blog-zh" */ "@/data/blog.zh.js"),
  ar: () => import(/* webpackChunkName: "blog-ar" */ "@/data/blog.ar.js"),
};

/** Async: load + cache the dictionary for a locale. Falls back to NL. */
export async function getBlogAsync(localeCode) {
  if (!localeCode || localeCode === "nl") return BLOG_NL;
  if (cache[localeCode]) return cache[localeCode];
  if (pending[localeCode]) return pending[localeCode];
  const loader = loaders[localeCode];
  if (!loader) return BLOG_NL;
  pending[localeCode] = loader()
    .then((mod) => {
      const data = mod.BLOG || mod.default || BLOG_NL;
      cache[localeCode] = data;
      delete pending[localeCode];
      return data;
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn(`[blog] failed to load locale ${localeCode}:`, err?.message);
      cache[localeCode] = BLOG_NL;
      delete pending[localeCode];
      return BLOG_NL;
    });
  return pending[localeCode];
}

/** Synchronous accessor — returns NL fallback if the locale is not yet cached. */
export function getBlog(localeCode) {
  return cache[localeCode] || BLOG_NL;
}

/** Localize a single blog entry — falls back to NL field if missing. */
export function localizeBlog(slug, localeCode) {
  const localeDict = getBlog(localeCode);
  return localeDict[slug] || BLOG_NL[slug];
}

/**
 * True when the blog with this slug has NO translation in the requested
 * locale (so the page is showing NL fallback content). Always false for the
 * default Dutch locale. Note: returns true while async-loading because
 * `cache[localeCode]` is undefined until the import resolves.
 */
export function isBlogFallback(slug, localeCode) {
  if (!localeCode || localeCode === "nl") return false;
  const localeDict = cache[localeCode];
  if (!localeDict || localeDict === BLOG_NL) return true;
  return !localeDict[slug];
}

// Re-export available locale codes (used by router prefix detection)
export const SUPPORTED_LOCALES = ["nl", ...LOCALES];
